import { useRef, useState, type DragEvent } from 'react';
import { AlertTriangle, FileCheck2, FileUp, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/cn';
import { leggiFileProgramma } from '../../lib/eventi/fileProgramma';
import { dataOraIt } from '../../lib/eventi/stati';
import type { ProgrammaEvento } from '@eventi/tipi.ts';

type Props = {
  programma: ProgrammaEvento;
  importatoIl: string | null;
  /** Quante persone hanno gia' una richiesta o un'assegnazione: un nuovo file puo' toccarle. */
  conScelte: number;
  onImporta: (programma: ProgrammaEvento) => Promise<void>;
};

const quanti = (n: number, uno: string, tanti: string) => `${n} ${n === 1 ? uno : tanti}`;

/** Il contenuto di un programma in pastiglie: giorni, turni, aule, incontri da scegliere. */
function Contenuto({ programma }: { programma: ProgrammaEvento }) {
  const voci = [
    quanti(programma.giorni.length, 'giorno', 'giorni'),
    quanti(programma.turni.length, 'turno', 'turni'),
    quanti(programma.aule.length, 'aula', 'aule'),
    quanti(programma.sessioni.filter((s) => s.sceglibile).length, 'incontro da scegliere', 'incontri da scegliere'),
  ];
  return (
    <span className="flex flex-wrap gap-1.5">
      {voci.map((v) => (
        <span key={v} className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-medium text-neutral-700">
          {v}
        </span>
      ))}
    </span>
  );
}

/**
 * Import del programma dal file JSON esportato dall'app dell'evento. Il file
 * si sceglie o si trascina; si controlla subito e si importa solo dopo aver
 * visto cosa contiene.
 */
export function ImportaProgramma({ programma, importatoIl, conScelte, onImporta }: Props) {
  const campoFile = useRef<HTMLInputElement>(null);
  const [errori, setErrori] = useState<string[]>([]);
  const [pronto, setPronto] = useState<{ nome: string; programma: ProgrammaEvento } | null>(null);
  const [importando, setImportando] = useState(false);
  const [trascinando, setTrascinando] = useState(false);

  const scegliFile = async (file: File | undefined) => {
    setErrori([]);
    setPronto(null);
    if (!file) return;
    const esito = leggiFileProgramma(await file.text());
    if (esito.ok) setPronto({ nome: file.name, programma: esito.programma });
    else setErrori(esito.errori);
    if (campoFile.current) campoFile.current.value = '';
  };

  const importa = async () => {
    if (!pronto) return;
    setImportando(true);
    try {
      await onImporta(pronto.programma);
      setPronto(null);
    } catch {
      // L'avviso con il motivo lo mostra la scheda; il file resta pronto per riprovare.
    } finally {
      setImportando(false);
    }
  };

  const trascina = {
    onDragOver: (e: DragEvent) => {
      e.preventDefault();
      setTrascinando(true);
    },
    onDragLeave: () => setTrascinando(false),
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      setTrascinando(false);
      void scegliFile(e.dataTransfer.files?.[0]);
    },
  };
  const apriScelta = () => campoFile.current?.click();

  return (
    <div className="space-y-3">
      {importatoIl ? (
        <div
          {...trascina}
          className={cn(
            'flex flex-col gap-4 rounded-xl border bg-white p-4 shadow-sm md:p-5 sm:flex-row sm:items-center',
            trascinando ? 'border-primary-400 ring-2 ring-primary-100' : 'border-gray-200',
          )}
        >
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-green-50 text-green-600">
              <FileCheck2 className="h-5 w-5" />
            </span>
            <div className="min-w-0 space-y-1.5">
              <p className="text-sm font-semibold text-gray-900">Programma importato il {dataOraIt(importatoIl)}</p>
              <Contenuto programma={programma} />
              <p className="text-[11px] text-neutral-500">
                Se il programma cambia, carica il file nuovo (anche trascinandolo qui): i posti che avete deciso restano.
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={apriScelta} className="flex-shrink-0">
            <FileUp className="mr-2 h-4 w-4" />
            Carica un file aggiornato
          </Button>
        </div>
      ) : (
        <button
          type="button"
          {...trascina}
          onClick={apriScelta}
          className={cn(
            'w-full rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors md:py-14',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            trascinando ? 'border-primary-400 bg-primary-50/50' : 'border-gray-300 bg-white hover:border-primary-300 hover:bg-gray-50',
          )}
        >
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-600">
            <FileUp className="h-6 w-6" />
          </span>
          <span className="mt-3 block text-base font-semibold text-gray-900">Carica il programma dell'evento</span>
          <span className="mx-auto mt-1 block max-w-md text-sm leading-relaxed text-neutral-500">
            Trascina qui il file .json preparato dall'app dell'evento, oppure clicca per sceglierlo. Prima di importarlo
            ti mostro cosa contiene.
          </span>
        </button>
      )}
      <input
        ref={campoFile}
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={(e) => void scegliFile(e.target.files?.[0])}
      />

      {errori.length > 0 && (
        <div className="flex gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-xs text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <p className="mb-1 font-semibold">Questo file non si può importare:</p>
            <ul className="list-disc space-y-0.5 pl-4">
              {errori.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {pronto && (
        <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900">
          <div className="space-y-1.5">
            <p className="text-sm font-semibold text-gray-900">Pronto da importare: {pronto.nome}</p>
            <Contenuto programma={pronto.programma} />
          </div>
          <p className="leading-relaxed">
            Gli incontri che nel file non ci sono più vengono tolti dal programma.
            {conScelte > 0 &&
              ` ${conScelte} persone hanno già una richiesta o un'assegnazione: se un loro incontro sparisce, lo vedrete segnato come "non più in programma".`}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setPronto(null)} disabled={importando} className="bg-white">
              Annulla
            </Button>
            <Button size="sm" onClick={importa} disabled={importando} className="bg-primary-600 text-white hover:bg-primary-700">
              {importando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Importa il programma
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
